import React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useDocuments } from 'contexts/documents'
import { useCategories } from 'pages/documents/utilities'
import { useDocumentSigningStatusTextFormatter } from 'pages/documents/signing-status'
import { useAccess } from 'contexts/access'
import { useOrganization } from 'contexts/organization'
import DocumentType from 'pages/documents/components/document-type'
import { basicApplied, advancedApplied } from 'components/filter/utilities'
import { compact } from 'utilities/array'
import Paragraph from 'components/typography/paragraph'
import SigningStatusLabel from 'pages/documents/signing-status/label'
import Filter from 'components/filter'

const DocumentsFilter = ({ context = 'default', ...props }) => {
    const { formatMessage } = useIntl()

    const {
        filter,
        setDocumentsFilter: setFilter,
        dokuments: items,
        fetching,
        hasFetched
    } = useDocuments()

    const { categories } = useCategories()
    const valueLabelFormatter = useDocumentSigningStatusTextFormatter()

    const { checkFeature } = useAccess()
    const signing = checkFeature('digital-signing')
    const smartDocumentsAvailable = checkFeature('smart-documents')

    const { organization } = useOrganization()

    const basicFields = [
        {
            type: 'search',
            name: 'search',
            controlProps: {
                placeholder: formatMessage({
                    id: 'document_action_find_by_title',
                    defaultMessage: 'Find document by title'
                })
            }
        }
    ]

    const advancedFields = compact([
        !!categories?.length && {
            type: 'checkboxes',
            name: 'category',
            label: formatMessage({
                id: 'noun_category',
                defaultMessage: 'Category'
            }),
            field: {
                options: categories.map(({
                    value: id,
                    text: name,
                    ...category
                }) => ({
                    ...category,
                    id,
                    name
                }))
            },
            empty: []
        },
        {
            type: 'checkboxes',
            name: 'contentTypeGroup',
            label: formatMessage({
                id: 'noun_type',
                defaultMessage: 'Type'
            }),
            field: {
                options: [
                    !!smartDocumentsAvailable && {
                        id: 'none',
                        name: <DocumentType type="smart" />
                    },
                    {
                        id: 'pdf',
                        name: <DocumentType type="pdf" />
                    },
                    {
                        id: 'richtext',
                        name: <DocumentType type="rich" />
                    },
                    {
                        id: 'plaintext',
                        name: <DocumentType type="text" />
                    },
                    {
                        id: 'spreadsheet',
                        name: <DocumentType type="spreadsheet" />
                    },
                    {
                        id: 'presentation',
                        name: <DocumentType type="presentation" />
                    },
                    {
                        id: 'image',
                        name: <DocumentType type="image" />
                    },
                    {
                        id: 'professional',
                        name: <DocumentType type="design" />
                    },
                    {
                        id: 'video',
                        name: <DocumentType type="video" />
                    },
                    {
                        id: 'audio',
                        name: <DocumentType type="audio" />
                    },
                    {
                        id: 'archive',
                        name: <DocumentType type="archive" />
                    }
                ]
            }
        },
        {
            type: 'shareUsers',
            name: 'concernsIds',
            label: formatMessage({
                id: 'document_label_regarding',
                defaultMessage: 'Regarding'
            }),
            empty: [],
            quickSelectMe: true
        },
        {
            type: 'shareUser',
            name: 'creatorId',
            label: formatMessage({
                id: 'label_created_by',
                defaultMessage: 'Created by'
            }),
            quickSelectMe: true
        },
        (!!organization && context !== 'group') && {
            type: 'shareOrEveryone',
            name: 'access',
            label: formatMessage({
                id: 'label_shared_with',
                defaultMessage: 'Shared with'
            }),
            empty: [],
            types: ['team', 'location', 'user'],
            shareLabel: formatMessage({
                id: 'label_groups_users_specific',
                defaultMessage: 'Specific groups and people'
            }),
            shareContent: !filter.access?.length ? (
                <Paragraph className="caption compact">
                    <FormattedMessage
                        id="documents_filter_access_default_information"
                        defaultMessage="Only documents directly shared with the selected people, teams and locations." />
                </Paragraph>
            ) : null,
            everyoneLabel: organization.name,
            everyoneContent: (
                <Paragraph className="caption compact">
                    <FormattedMessage
                        id="documents_filter_access_everyone_information"
                        defaultMessage="Only documents directly shared with {company}."
                        values={{ company: organization.name }} />
                </Paragraph>
            )
        },
        !!signing && {
            type: 'radiobuttons',
            name: 'signingStatus',
            label: formatMessage({
                id: 'documents_signing_status_label',
                defaultMessage: 'Signing status'
            }),
            getValueLabel: valueLabelFormatter,
            field: {
                options: [
                    {
                        value: 'any',
                        name: (
                            <Paragraph className="small compact">
                                <FormattedMessage
                                    id="document_signing_status_any"
                                    defaultMessage="All signature documents" />
                            </Paragraph>
                        )
                    },
                    ...[
                        'pending',
                        'closed',
                        'canceled',
                        'timedout',
                        'rejected',
                        'document_error'
                    ].map(status => ({
                        value: status,
                        name: <SigningStatusLabel status={status} />
                    }))
                ]
            }
        }
    ])

    return (
        <Filter
            {...props}
            filter={filter}
            setFilter={setFilter}
            basicFields={basicFields}
            advancedFields={advancedFields}
            items={items}
            fetching={fetching}
            hasFetched={hasFetched}
            context="documents"
            salt="documents" />
    )
}

const basicNames = ['search']
const advancedNames = ['category', 'concernsIds', 'creatorId', 'access', 'contentTypeGroup', 'signingStatus']

export const areBasicFiltersApplied = basicApplied(basicNames)
export const areAdvancedFiltersApplied = advancedApplied(advancedNames)

export const areFiltersApplied = filter => (
    areBasicFiltersApplied(filter) ||
    areAdvancedFiltersApplied(filter)
)

export default DocumentsFilter